import { Component, inject } from '@angular/core';

import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { SessionsService, SessionDto } from '../core/sessions.service';
import { AuthService } from '../core/auth.service';

interface SessionGroup {
  device: string;
  ipAddress?: string;
  latestCreatedAt: string;
  latestExpiresAt?: string;
  anyActive: boolean;
  sessions: SessionDto[];
}

@Component({
  standalone: true,
  selector: 'app-sessions-page',
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    TranslateModule,
  ],
  templateUrl: './sessions.component.html',
  styleUrls: ['./sessions.component.scss'],
})
export class SessionsComponent {
  api = inject(SessionsService);
  auth = inject(AuthService);
  rows: SessionDto[] = [];
  groups: SessionGroup[] = [];
  expandedRowKeys: Record<string, boolean> = {};

  constructor() {
    this.load();
  }

  load() {
    this.api.mySessions().subscribe((d) => {
      this.rows = d || [];
      this.groups = this.groupByDevice(this.rows);
    });
  }

  private groupByDevice(sessions: SessionDto[]): SessionGroup[] {
    const map = new Map<string, SessionGroup>();
    for (const s of sessions) {
      const key = s.device || 'Unknown';
      let g = map.get(key);
      if (!g) {
        g = {
          device: key,
          ipAddress: s.ipAddress,
          latestCreatedAt: s.createdAt,
          latestExpiresAt: s.expiresAt,
          anyActive: false,
          sessions: [],
        };
        map.set(key, g);
      }
      g.sessions.push(s);
      if (s.isActive) g.anyActive = true;
      if (new Date(s.createdAt).getTime() > new Date(g.latestCreatedAt).getTime()) {
        g.latestCreatedAt = s.createdAt;
        g.latestExpiresAt = s.expiresAt;
        g.ipAddress = s.ipAddress;
      }
    }
    for (const g of map.values()) {
      g.sessions.sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.latestCreatedAt).getTime() - new Date(a.latestCreatedAt).getTime(),
    );
  }

  /** Best-effort device icon from a user-agent-ish string. */
  deviceIcon(device: string): string {
    const d = (device || '').toLowerCase();
    if (/mobile|android|iphone|ipad|ipod/.test(d)) return 'pi pi-mobile';
    if (/tablet/.test(d)) return 'pi pi-tablet';
    return 'pi pi-desktop';
  }

  toggleRow(g: SessionGroup) {
    this.expandedRowKeys = {
      ...this.expandedRowKeys,
      [g.device]: !this.expandedRowKeys[g.device],
    };
  }

  revoke(r: SessionDto) {
    this.api.revokeMine(r.id).subscribe(() => this.load());
  }

  revokeGroup(g: SessionGroup) {
    const active = g.sessions.filter((s) => s.isActive);
    if (active.length === 0) return;
    let remaining = active.length;
    for (const s of active) {
      this.api.revokeMine(s.id).subscribe({
        next: () => {
          if (--remaining === 0) this.load();
        },
        error: () => {
          if (--remaining === 0) this.load();
        },
      });
    }
  }

  revokeAll() {
    this.api.revokeAllMine().subscribe(() => this.load());
  }
}
